/**
 * Shared checklist set editor (SS default or unit-type).
 * Tabs: Overview · Edit · Add question · Preview on app · Info
 */
import { useEffect, useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  Undo2,
  Save,
  Check,
  ImagePlus,
  Smartphone,
  Info,
  List,
  Pencil,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { parseChecklistImportText, readFileAsText } from '../utils/parseChecklistImport';
import ChecklistSetDocuments from './ChecklistSetDocuments';
import AuthImage from './AuthImage';
import AppChecklistPreview from './AppChecklistPreview';
import { englishizeItem } from '../utils/checklistKoEn';

const TABS = [
  { id: 'overview', label: 'Overview', icon: List },
  { id: 'edit', label: 'Edit', icon: Pencil },
  { id: 'add', label: 'Add question', icon: Plus },
  { id: 'preview', label: 'Preview on app', icon: Smartphone },
  { id: 'info', label: 'Info', icon: Info },
];

const emptyItem = () => ({
  question: '',
  instruction: '',
  info: '',
  inspection_criteria: '',
  category: '',
  method_tool: '',
  unit: '',
  value_type: 'BOOL',
  pass_threshold: '',
  source: 'CUSTOM',
  guidance_images: [],
});

function typeHint(vt) {
  if (vt === 'NUM') return 'Number — auto Pass/Fail vs threshold';
  if (vt === 'TEXT') return 'Text notes / measurements narrative';
  return 'Pass / Fail (tick)';
}

function typeLabel(vt) {
  if (vt === 'NUM') return 'Number';
  if (vt === 'TEXT') return 'Text';
  return 'Pass/Fail';
}

function ConfirmDialog({ title, message, onCancel, onOk, okLabel = 'OK', busy = false }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{message}</p>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onOk}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChecklistSetEditor({
  ssCode,
  ssName = '',
  scope = 'SS_DEFAULT',
  unitType = '',
  buildingId = null,
  title,
  subtitle,
  compact = false,
}) {
  const queryClient = useQueryClient();
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [addMode, setAddMode] = useState('manual');
  const [customItem, setCustomItem] = useState(emptyItem());
  const [importRows, setImportRows] = useState(null);
  const [importSelected, setImportSelected] = useState([]);
  const [importWarnings, setImportWarnings] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const importFileRef = useRef(null);

  const resetAddForm = () => {
    setAddMode('manual');
    setCustomItem(emptyItem());
    setImportRows(null);
    setImportSelected([]);
    setImportWarnings([]);
  };

  const qKey = ['checklist-set', ssCode, scope, unitType || '', buildingId || ''];

  const { data: view, isLoading, error } = useQuery({
    queryKey: qKey,
    queryFn: async () => {
      const res = await api.get('/checklist-sets', {
        params: {
          ssCode,
          scope,
          unitType: scope === 'UNIT_TYPE' ? unitType : undefined,
          buildingId: buildingId || undefined,
        },
      });
      return res.data?.data;
    },
    enabled: !!ssCode && (scope !== 'UNIT_TYPE' || !!unitType),
  });

  useEffect(() => {
    if (!view?.items) return;
    setItems(view.items.map((it, i) => englishizeItem({ ...it, item_order: i + 1 }, i)));
  }, [view]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: qKey });
    if (buildingId) {
      queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'ss-units'] });
      queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'ss-units', 'operational'] });
    }
  };

  const buildingOnly = !!buildingId;

  const saveMutation = useMutation({
    mutationFn: async ({ publish }) => {
      // Building Checklists page: Save = push to this building’s units only (never touch master).
      if (buildingOnly && !publish) {
        return api.post('/checklist-sets/apply-to-building', {
          buildingId,
          ssCode,
          unitType: scope === 'UNIT_TYPE' ? unitType : undefined,
          items,
        });
      }
      return api.put('/checklist-sets', {
        ssCode,
        scope,
        unitType: scope === 'UNIT_TYPE' ? unitType : undefined,
        buildingId,
        items,
        publish,
      });
    },
    onSuccess: (res, vars) => {
      invalidate();
      if (buildingOnly && !vars.publish) {
        const d = res.data?.data || {};
        toast.success(
          `Saved to this building only — ${d.units_updated ?? 0} unit(s) updated. Shared SS original unchanged.`,
          { duration: 6000 }
        );
        setConfirm(null);
        return;
      }
      const perms = res.data?.data?.permissions;
      if (vars.publish && perms?.needs_admin_approve === false) {
        toast.success(
          'Published to shared SS original — other buildings will use this on new/empty units.',
          { duration: 6000 }
        );
      } else if (vars.publish && res.data?.data?.set?.status === 'DRAFT') {
        toast.success('Saved as draft — waiting for admin approval');
      } else {
        toast.success(vars.publish ? 'Saved' : 'Draft saved');
      }
      setConfirm(null);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const approveMutation = useMutation({
    mutationFn: () =>
      api.post('/checklist-sets/approve', {
        ssCode,
        scope,
        unitType: scope === 'UNIT_TYPE' ? unitType : undefined,
      }),
    onSuccess: () => {
      invalidate();
      toast.success(
        'Approved — published for all buildings (next inspection sync picks it up automatically).',
        { duration: 6000 }
      );
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Approve failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: () =>
      api.post('/checklist-sets/restore-factory', {
        ssCode,
        scope,
        unitType: scope === 'UNIT_TYPE' ? unitType : undefined,
        buildingId,
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Restored factory default (previous version kept for Revert)');
      setConfirm(null);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Restore failed'),
  });

  const revertMutation = useMutation({
    mutationFn: () =>
      api.post('/checklist-sets/revert-previous', {
        ssCode,
        scope,
        unitType: scope === 'UNIT_TYPE' ? unitType : undefined,
        buildingId,
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Reverted to previous version');
      setConfirm(null);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Revert failed'),
  });

  const perms = view?.permissions || {};
  const canEdit = perms.can_edit !== false;

  const move = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[index], next[j]] = [next[j], next[index]];
    setItems(next.map((it, i) => ({ ...it, item_order: i + 1 })));
  };

  const updateItem = (index, patch) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== index) return it;
        const merged = { ...it, ...patch };
        if (patch.instruction !== undefined) merged.info = patch.instruction;
        return merged;
      })
    );
  };

  const uploadGuidanceImage = async (index, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('type', 'checklist-guidance');
      fd.append('file', file);
      const res = await api.post('/files/upload', fd, {
        headers: { 'X-Upload-Type': 'checklist-guidance' },
      });
      const filename = res.data?.filename;
      if (!filename) throw new Error('Upload returned no filename');
      const cur = items[index] || {};
      const imgs = Array.isArray(cur.guidance_images) ? [...cur.guidance_images] : [];
      imgs.push({
        filename,
        caption: '',
        url: res.data?.url || `/api/files/${filename}`,
      });
      updateItem(index, { guidance_images: imgs });
      toast.success('Guidance photo added — Save / Publish from Edit to keep');
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Upload failed');
    }
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index).map((it, i) => ({ ...it, item_order: i + 1 })));
  };

  const addCustom = () => {
    if (!customItem.question.trim()) return;
    setItems((prev) => [
      ...prev,
      englishizeItem({ ...customItem, id: `tmp-${Date.now()}`, item_order: prev.length + 1 }, prev.length),
    ]);
    resetAddForm();
    toast.success('Question added — open Edit to Save draft or Publish');
  };

  const askRestore = () => {
    setConfirm({
      kind: 'restore',
      title: 'Load detailed defaults?',
      message:
        'This replaces the current list with the full English factory inspection steps, criteria, and Pass/Fail thresholds.\n\nYour previous version is kept — use Revert if you need it back.',
      okLabel: 'OK',
    });
  };

  const askRevert = () => {
    setConfirm({
      kind: 'revert',
      title: 'Revert previous version?',
      message:
        'This will discard the current checklist and restore the last saved previous version.\n\nThis cannot be undone from here. Continue?',
      okLabel: 'OK',
    });
  };

  const askPublish = () => {
    const label = buildingOnly
      ? 'Publish shared original?'
      : perms.can_publish_direct
        ? 'Publish'
        : 'Submit for approval';
    setConfirm({
      kind: 'publish',
      title: `${label}`,
      message: buildingOnly
        ? 'This changes the SHARED SS original used by other buildings for new/empty units.\n\nIt does not replace what you already Saved to this building’s units.\n\nPrefer Save (this building only) unless you really mean to change the master list.'
        : perms.can_publish_direct
          ? 'Publishing makes this checklist the live shared version for buildings on the next inspection sync.\n\nMake sure questions, criteria, and thresholds are correct before you continue.'
          : 'This saves the checklist as a draft for admin approval.\n\nBuildings will not use it until it is approved.',
      okLabel: 'OK',
    });
  };

  const runConfirm = () => {
    if (!confirm) return;
    if (confirm.kind === 'restore') restoreMutation.mutate();
    else if (confirm.kind === 'revert') revertMutation.mutate();
    else if (confirm.kind === 'publish') saveMutation.mutate({ publish: true });
  };

  const confirmBusy =
    restoreMutation.isPending || revertMutation.isPending || saveMutation.isPending;

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const { items: parsed, warnings } = parseChecklistImportText(text, file.name);
      if (!parsed.length) {
        toast.error(warnings[0] || 'No questions found in file');
        setImportWarnings(warnings);
        return;
      }
      setImportRows(parsed);
      setImportSelected(parsed.map((_, i) => i));
      setImportWarnings(warnings || []);
    } catch (err) {
      toast.error(err.message || 'Could not read file');
    }
  };

  const applyImport = () => {
    if (!importRows?.length) return;
    const picked = importSelected
      .slice()
      .sort((a, b) => a - b)
      .map((i) => importRows[i])
      .filter(Boolean);
    if (!picked.length) {
      toast.error('Select at least one row');
      return;
    }
    setItems((prev) => [
      ...prev,
      ...picked.map((it, i) =>
        englishizeItem({ ...it, item_order: prev.length + i + 1 }, prev.length + i)
      ),
    ]);
    resetAddForm();
    toast.success(
      `Added ${picked.length} question${picked.length === 1 ? '' : 's'} — open Edit to Save draft or Publish`
    );
  };

  const selectTab = (id) => {
    setActiveTab(id);
    if (id === 'add') resetAddForm();
    if (id === 'preview') {
      // Always open the phone frame — empty list shows a calm empty state (no error toast).
      setPreviewOpen(true);
    }
  };

  if (!ssCode) {
    return <p className="text-sm text-gray-500">Select an SS code.</p>;
  }

  if (isLoading) return <p className="text-sm text-gray-500">Loading checklist…</p>;
  if (error) {
    return (
      <p className="text-sm text-red-600">
        {error.response?.data?.error || error.message || 'Failed to load checklist set'}
      </p>
    );
  }

  const heading = title || [ssCode, ssName].filter(Boolean).join(' ') || ssCode;
  const sub =
    subtitle != null
      ? subtitle
      : scope === 'UNIT_TYPE'
        ? unitType || ''
        : 'SS default checklist';

  const renderOverview = () => {
    if (!items.length) {
      return (
        <div className="bg-card rounded-lg border border-gray-200 p-6 text-sm text-gray-600 space-y-2">
          <p>No questions in this checklist yet.</p>
          <p>
            Use <strong>Add question</strong> to create items, or open <strong>Edit</strong> and choose{' '}
            <strong>Load detailed defaults</strong> for the full English factory steps.
          </p>
        </div>
      );
    }
    return (
      <div className="bg-card rounded-lg border border-gray-200 overflow-hidden">
        <ul className="divide-y divide-gray-100">
          {items.map((t, i) => {
            const info = t.instruction || t.info || '';
            return (
              <li key={t.id || i} className="p-4 hover:bg-slate-50/80">
                <div className="flex gap-3">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      {t.category ? (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                          {t.category}
                        </span>
                      ) : null}
                      <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        {typeLabel(t.value_type)}
                      </span>
                      {(t.field_added || t.origin === 'FIELD') && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">
                          Field-added
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{t.question || 'Untitled'}</p>
                    {info ? (
                      <p className="text-xs text-gray-600 whitespace-pre-wrap line-clamp-3">{info}</p>
                    ) : null}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 pt-0.5">
                      {t.inspection_criteria ? (
                        <span>
                          Criteria: <span className="text-gray-700">{t.inspection_criteria}</span>
                        </span>
                      ) : null}
                      {t.method_tool ? (
                        <span>
                          Method: <span className="text-gray-700">{t.method_tool}</span>
                        </span>
                      ) : null}
                      {t.unit ? (
                        <span>
                          Unit: <span className="text-gray-700">{t.unit}</span>
                        </span>
                      ) : null}
                      {(t.value_type || 'BOOL') === 'NUM' && t.pass_threshold ? (
                        <span className="text-blue-700">
                          Threshold: <code>{t.pass_threshold}</code>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderEditList = () => {
    if (!items.length) {
      return (
        <p className="p-4 text-sm text-gray-500">
          No questions yet. Use <strong>Add question</strong>, or <strong>Load detailed defaults</strong> below.
        </p>
      );
    }
    return (
      <ul className="divide-y divide-gray-100">
        {items.map((t, i) => (
          <li key={t.id || i} className="p-3 flex items-start gap-2">
            <div className="flex flex-col shrink-0 pt-1">
              <button
                type="button"
                disabled={!canEdit || i === 0}
                onClick={() => move(i, -1)}
                className="p-0.5 text-gray-500 disabled:opacity-30"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                disabled={!canEdit || i === items.length - 1}
                onClick={() => move(i, 1)}
                className="p-0.5 text-gray-500 disabled:opacity-30"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-400 w-6">{i + 1}</span>
                <input
                  disabled={!canEdit}
                  className="flex-1 min-w-[10rem] border border-gray-200 rounded px-2 py-1.5 text-sm font-semibold"
                  value={t.question || ''}
                  onChange={(e) => updateItem(i, { question: e.target.value })}
                  placeholder="Inspection item title"
                />
                <select
                  disabled={!canEdit}
                  className="border border-gray-200 rounded px-2 py-1.5 text-xs"
                  value={t.value_type || 'BOOL'}
                  onChange={(e) => updateItem(i, { value_type: e.target.value })}
                  title={typeHint(t.value_type)}
                >
                  <option value="BOOL">Pass/Fail</option>
                  <option value="NUM">Number (auto P/F)</option>
                  <option value="TEXT">Text notes</option>
                </select>
              </div>
              <textarea
                disabled={!canEdit}
                rows={2}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-gray-800"
                value={t.instruction || t.info || ''}
                onChange={(e) => updateItem(i, { instruction: e.target.value })}
                placeholder="Info — on-site how-to"
              />
              <div className="flex flex-wrap gap-2 items-center">
                {(t.guidance_images || []).map((g, gi) => (
                  <div key={g.filename || gi} className="relative w-20 h-20">
                    <AuthImage
                      filename={g.filename}
                      alt={g.caption || 'Guidance'}
                      className="w-20 h-20 object-cover rounded border"
                    />
                    {canEdit && (
                      <button
                        type="button"
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white text-xs leading-none"
                        onClick={() => {
                          const imgs = (t.guidance_images || []).filter((_, j) => j !== gi);
                          updateItem(i, { guidance_images: imgs });
                        }}
                        title="Remove photo"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <label className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-dashed border-gray-300 text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                    <ImagePlus className="w-4 h-4" />
                    Add info photo
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        uploadGuidanceImage(i, f);
                      }}
                    />
                  </label>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <input
                  disabled={!canEdit}
                  className="border border-gray-200 rounded px-2 py-1.5 text-sm"
                  value={t.category || ''}
                  onChange={(e) => updateItem(i, { category: e.target.value })}
                  placeholder="Category"
                />
                <input
                  disabled={!canEdit}
                  className="border border-gray-200 rounded px-2 py-1.5 text-sm"
                  value={t.method_tool || ''}
                  onChange={(e) => updateItem(i, { method_tool: e.target.value })}
                  placeholder="Method / tool"
                />
                <input
                  disabled={!canEdit}
                  className="border border-gray-200 rounded px-2 py-1.5 text-sm"
                  value={t.inspection_criteria || ''}
                  onChange={(e) => updateItem(i, { inspection_criteria: e.target.value })}
                  placeholder="Pass criteria / standard"
                />
                <div className="flex gap-1">
                  <input
                    disabled={!canEdit}
                    className="w-20 border border-gray-200 rounded px-2 py-1.5 text-sm"
                    value={t.unit || ''}
                    onChange={(e) => updateItem(i, { unit: e.target.value })}
                    placeholder="Unit"
                  />
                  <input
                    disabled={!canEdit || (t.value_type || 'BOOL') !== 'NUM'}
                    className="flex-1 border border-blue-200 rounded px-2 py-1.5 text-sm text-blue-900 disabled:opacity-40"
                    value={t.pass_threshold || ''}
                    onChange={(e) => updateItem(i, { pass_threshold: e.target.value })}
                    placeholder="e.g. <=67"
                    title="Auto Pass/Fail threshold for numeric readings"
                  />
                </div>
              </div>
              {(t.value_type || 'BOOL') === 'NUM' && t.pass_threshold && (
                <p className="text-[11px] text-blue-700">
                  Auto Pass if measured value meets <code>{t.pass_threshold}</code>
                  {t.unit ? ` ${t.unit}` : ''} — otherwise Fail
                </p>
              )}
              {(t.field_added || t.origin === 'FIELD') && (
                <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">
                  Field-added
                </span>
              )}
            </div>
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => removeItem(i)}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-40"
              title="Remove"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    );
  };

  const renderAdd = () => (
    <div className="bg-card rounded-lg border border-gray-200 p-4 sm:p-5 space-y-4">
      <div>
        <p className="text-xs font-medium text-gray-600 mb-2">How do you want to add?</p>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-semibold max-w-md">
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => {
              setAddMode('manual');
              setImportRows(null);
              setImportSelected([]);
              setImportWarnings([]);
            }}
            className={`flex-1 py-2.5 disabled:opacity-50 ${
              addMode === 'manual' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Manual input
          </button>
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => setAddMode('file')}
            className={`flex-1 py-2.5 border-l border-gray-200 disabled:opacity-50 ${
              addMode === 'file' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            File upload
          </button>
        </div>
      </div>

      {addMode === 'manual' ? (
        <div className="space-y-3 max-w-xl">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Inspection item title</label>
            <input
              type="text"
              disabled={!canEdit}
              value={customItem.question || ''}
              onChange={(e) => setCustomItem((p) => ({ ...p, question: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Info (on-site how-to)</label>
            <textarea
              rows={3}
              disabled={!canEdit}
              value={customItem.instruction || ''}
              onChange={(e) => setCustomItem((p) => ({ ...p, instruction: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
              placeholder="e.g. Inspect each emergency luminaire and EXIT sign. Count failed fittings."
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Answer type</label>
              <select
                disabled={!canEdit}
                value={customItem.value_type || 'BOOL'}
                onChange={(e) => setCustomItem((p) => ({ ...p, value_type: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
              >
                <option value="BOOL">Pass / Fail</option>
                <option value="NUM">Number (auto Pass/Fail)</option>
                <option value="TEXT">Text notes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pass threshold</label>
              <input
                type="text"
                value={customItem.pass_threshold || ''}
                onChange={(e) => setCustomItem((p) => ({ ...p, pass_threshold: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. <=67 or >=1.0"
                disabled={!canEdit || (customItem.value_type || 'BOOL') !== 'NUM'}
              />
            </div>
          </div>
          {['category', 'method_tool', 'inspection_criteria', 'unit'].map((key) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                {key === 'method_tool'
                  ? 'Method / tool'
                  : key === 'inspection_criteria'
                    ? 'Pass criteria / standard'
                    : key}
              </label>
              <input
                type="text"
                disabled={!canEdit}
                value={customItem[key] || ''}
                onChange={(e) => setCustomItem((p) => ({ ...p, [key]: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:opacity-50"
              />
            </div>
          ))}
          <button
            type="button"
            disabled={!canEdit || !customItem.question.trim()}
            onClick={addCustom}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      ) : (
        <div className="space-y-3 max-w-2xl">
          <p className="text-sm text-gray-600">
            Upload a CSV or JSON checklist file. Preview rows, tick which to add, then Save.
          </p>
          <input
            ref={importFileRef}
            type="file"
            disabled={!canEdit}
            accept=".csv,.json,.txt,text/csv,application/json"
            onChange={onImportFile}
            className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary-50 file:text-primary-800 file:font-semibold file:cursor-pointer disabled:opacity-50"
          />
          {importWarnings.length > 0 && (
            <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 list-disc pl-5">
              {importWarnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          {importRows && importRows.length > 0 && (
            <>
              <div className="flex gap-2 text-xs items-center">
                <button
                  type="button"
                  className="underline text-primary-700"
                  onClick={() => setImportSelected(importRows.map((_, i) => i))}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="underline text-gray-600"
                  onClick={() => setImportSelected([])}
                >
                  Clear
                </button>
                <span className="text-gray-400 ml-auto">
                  {importSelected.length} / {importRows.length} selected
                </span>
              </div>
              <ul className="max-h-56 overflow-y-auto border border-gray-200 rounded divide-y divide-gray-100">
                {importRows.map((row, i) => {
                  const on = importSelected.includes(i);
                  return (
                    <li key={row.id || i} className="p-2.5 flex gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => {
                          setImportSelected((prev) =>
                            on ? prev.filter((x) => x !== i) : [...prev, i].sort((a, b) => a - b)
                          );
                        }}
                        className="mt-1"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900">{row.question}</p>
                        {row.instruction ? (
                          <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{row.instruction}</p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                disabled={!canEdit || !importSelected.length}
                onClick={applyImport}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> Save
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderPreviewTab = () => (
    <div className="bg-card rounded-lg border border-gray-200 p-6 space-y-3 max-w-lg">
      <p className="text-sm text-gray-700">
        The phone preview opens from the <strong>Preview on app</strong> tab above (including unsaved edits).
        Close it with X when finished.
      </p>
      {!items.length ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          No questions in this SS yet — the phone frame shows an empty-state message. Add questions, then open the
          tab again.
        </p>
      ) : null}
    </div>
  );

  const renderInfo = () => (
    <div className="bg-card rounded-lg border border-gray-200 p-5 sm:p-6 space-y-5 max-w-3xl text-sm text-gray-700">
      <section className="space-y-2">
        <h3 className="text-base font-semibold text-gray-900">What this checklist is</h3>
        <p>
          This is the shared question set for this SS (or equipment type). Workers answer each item on the app with
          Pass, Fail, or N/A.
          {buildingOnly ? (
            <>
              {' '}
              On a <strong>building</strong> page, <strong>Save</strong> writes these questions to{' '}
              <strong>this building’s units only</strong> (phone picks them up after Sync). It does{' '}
              <strong>not</strong> change the shared SS original. Use Publish only if you intentionally want to
              update the master list for other buildings.
            </>
          ) : (
            <> Published changes apply to buildings on the next inspection sync.</>
          )}
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-gray-900">Tabs</h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong>Overview</strong> — read-only list of current questions for a quick scan.
          </li>
          <li>
            <strong>Edit</strong> — change existing questions, reorder, or remove them.
            {buildingOnly ? (
              <>
                {' '}
                <strong>Save</strong> applies to this building’s units only (shared original unchanged).{' '}
                <strong>Publish shared original</strong> updates the master list for other buildings — use only when
                intentional.
              </>
            ) : (
              <>
                {' '}
                Use <strong>Save</strong> for a draft, <strong>Publish</strong> to go live (or submit for approval),
                and <strong>Revert</strong> to restore the previous saved version. Both Publish and Revert ask for
                confirmation first.
              </>
            )}
          </li>
          <li>
            <strong>Add question</strong> — create one item manually, or upload a CSV/JSON file and select rows.
            <strong> Save</strong> adds them to the list; open Edit afterward to Save draft or Publish.
          </li>
          <li>
            <strong>Preview on app</strong> — phone-sized preview of the worker inspection card.
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-gray-900">Question fields</h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong>Category</strong> — short group label shown above the question on the app (e.g. Visual,
            Functional, Documentation).
          </li>
          <li>
            <strong>Title (question)</strong> — the main check the worker must answer.
          </li>
          <li>
            <strong>Info</strong> — on-site how-to text shown under the title (methods, what to look for). Optional
            guidance photos can be attached in Edit.
          </li>
          <li>
            <strong>Pass criteria / standard</strong> — the pass rule or clause (NZS, CS, manufacturer) used as the
            judgment reference.
          </li>
          <li>
            <strong>Method / tool</strong> — optional note on how or with what tool the check is done.
          </li>
          <li>
            <strong>Answer type</strong> — Pass/Fail (tick), Number (auto Pass/Fail vs threshold), or Text notes.
          </li>
          <li>
            <strong>Unit</strong> — measurement unit for numeric items (e.g. °C, lux, mm). Shown next to the reading
            field on the app.
          </li>
          <li>
            <strong>Pass threshold</strong> — for Number type only. Example: <code>&lt;=67</code> means the measured
            value must be less than or equal to 67 to Pass; otherwise Fail. Also supports forms like{' '}
            <code>&gt;=1.0</code>.
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-gray-900">File upload (Add question)</h3>
        <p>
          Import a CSV or JSON file of checklist rows. After upload you can preview and tick which rows to add.
          Typical columns include question/title, info/instruction, category, criteria, unit, value type, and
          threshold. Warnings appear if a row is incomplete or a column is missing.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-gray-900">Load detailed defaults</h3>
        <p>
          Available under Edit. Replaces the current list with the full English factory inspection steps for this
          SS. The previous version is kept so you can Revert if needed.
        </p>
      </section>
    </div>
  );

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <div>
        <h2 className="text-xl font-bold text-gray-900 leading-tight">{heading}</h2>
        {sub ? <p className="text-sm text-gray-600 mt-0.5">{sub}</p> : null}
      </div>

      <nav
        className="flex flex-wrap gap-1 border-b border-gray-200 pb-px"
        aria-label="Checklist sections"
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const on = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-t-lg border-b-2 -mb-px transition-colors ${
                on
                  ? 'border-primary-600 text-primary-800 bg-primary-50/60'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-3.5 h-3.5 opacity-80" />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activeTab === 'overview' && renderOverview()}

      {activeTab === 'edit' && (
        <div className="space-y-3">
          <div className="bg-card rounded-lg border border-gray-200 overflow-hidden">{renderEditList()}</div>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              type="button"
              disabled={!canEdit || saveMutation.isPending}
              onClick={() => saveMutation.mutate({ publish: false })}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-semibold"
            >
              <Save className="w-4 h-4" />
              {buildingOnly ? 'Save to this building' : 'Save'}
            </button>
            {!buildingOnly ? (
              <button
                type="button"
                disabled={!canEdit || !view?.previous_items || revertMutation.isPending}
                onClick={askRevert}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-semibold"
              >
                <Undo2 className="w-4 h-4" /> Revert
              </button>
            ) : null}
            <button
              type="button"
              disabled={!canEdit || saveMutation.isPending}
              onClick={askPublish}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-emerald-600 text-emerald-800 rounded-lg hover:bg-emerald-50 disabled:opacity-50 font-semibold"
              title={
                buildingOnly
                  ? 'Changes the shared SS original for other buildings — not required for this building'
                  : undefined
              }
            >
              <Check className="w-4 h-4" />
              {buildingOnly
                ? 'Publish shared original…'
                : perms.can_publish_direct
                  ? 'Publish'
                  : 'Submit for approval'}
            </button>
            {view?.set?.has_draft && (perms.can_publish_direct || !perms.needs_admin_approve) && (
              <button
                type="button"
                disabled={approveMutation.isPending}
                onClick={() => approveMutation.mutate()}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50"
              >
                Approve draft
              </button>
            )}
            <button
              type="button"
              disabled={!canEdit || restoreMutation.isPending}
              onClick={askRestore}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-amber-500 text-amber-800 rounded-lg hover:bg-amber-50 disabled:opacity-50"
              title="Replace with factory default"
            >
              <RotateCcw className="w-4 h-4" /> Load detailed defaults
            </button>
          </div>
        </div>
      )}

      {activeTab === 'add' && renderAdd()}
      {activeTab === 'preview' && renderPreviewTab()}
      {activeTab === 'info' && renderInfo()}

      <ChecklistSetDocuments ssCode={ssCode} scope={scope} unitType={unitType} />

      {previewOpen ? (
        <AppChecklistPreview
          ssCode={ssCode}
          ssName={ssName || heading}
          items={items}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}

      {confirm ? (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          okLabel={confirm.okLabel || 'OK'}
          busy={confirmBusy}
          onCancel={() => setConfirm(null)}
          onOk={runConfirm}
        />
      ) : null}
    </div>
  );
}
